import { useState, useEffect, useCallback } from 'react';
import { addDays, startOfWeek, format } from 'date-fns';
import { DndContext } from '@dnd-kit/core';
import api from '../services/api';
import { useBranch } from '../context/BranchContext';
import VehicleCard from './VehicleCard';
import TransportOrderCard from './transport-new/TransportOrderCard';
import DriverHeaderBar from './transport-new/DriverHeaderBar';
import CompactDateBar from './transport-new/CompactDateBar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Read-only preview of the NEW Transport UI (same VehicleCard/TransportOrderCard/
 * DriverHeaderBar the full /transport page uses in View Mode) shown inside the
 * global header's Transport dropdown. Previously rendered the retired classic
 * ViewModeGrid — now shares the exact same components as the real page, just
 * with every editing/drag capability switched off (`isViewMode` on VehicleCard)
 * and all state-changing callbacks reduced to no-ops.
 *
 * Cost-aware design (the user explicitly asked for "credits to a minimum"):
 *   • Only fetches /api/logs/{date} + /api/settings on mount and when the
 *     selected date changes — no polling, no extra Zoho lookups.
 */
const noop = () => {};

const EmbeddedTransportView = ({ user, onNavigate }) => {
  const { getEffectiveBranchId } = useBranch();
  const currentBranch = getEffectiveBranchId() || 'C30';

  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [vehicles, setVehicles] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const dateStr = format(selectedDate, 'yyyy-MM-dd');
  const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
  const weekDays = Array.from({ length: 5 }, (_, i) => addDays(weekStart, i)); // Mon-Fri

  const loadDay = useCallback(async (date) => {
    const dStr = format(date, 'yyyy-MM-dd');
    setLoading(true);
    try {
      // Try the daily log first — has driver/run entries when present.
      // Falls back to the vehicle template (just registrations + assigned
      // drivers, empty runs) when no log has been created for the date so
      // the dropdown still shows the vehicle cards on quiet days.
      let vehiclesPayload = [];
      try {
        const logRes = await api.get(`${API}/logs/${dStr}?view_as_branch=${currentBranch}`);
        vehiclesPayload = logRes.data?.vehicles || [];
      } catch (_err) {
        const vRes = await api.get(`${API}/settings/vehicles-for-date/${currentBranch}/${dStr}`);
        vehiclesPayload = (vRes.data?.vehicles || []).map(v => ({
          ...v,
          runs: v.runs || [],
          entries: v.entries || [],
        }));
      }
      setVehicles(vehiclesPayload);
      if (!settings) {
        try {
          const settingsRes = await api.get(`${API}/settings`);
          setSettings(settingsRes.data || {});
        } catch (_) { setSettings({}); }
      }
    } catch (_) {
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  }, [currentBranch, settings]);

  useEffect(() => {
    loadDay(selectedDate);
  }, [selectedDate, currentBranch, loadDay]);

  // Props shared by every read-only <VehicleCard> — mirrors TransportNewUI's own
  // `vehicleCardCommonProps`, minus anything editing-related (all no-ops, since
  // `isViewMode` already keeps VehicleCard from calling most of them anyway).
  const vehicleCardCommonProps = {
    allVehicles: vehicles, onUpdate: noop, onUpdateMultiple: noop, onDelete: noop,
    userRole: user?.role, user, canEditVehicleDetails: false, settings: settings || {},
    queueVehicleSave: noop, currentDate: dateStr, onUnplanned: noop, onEntryAdded: noop,
    vehicleIndex: 0, globalMoveMode: false, onEnterMoveMode: noop, onExecuteMove: noop,
    onExitMoveMode: noop, isMultiMoveMode: false, onEnterMultiMoveMode: noop, onMultiMoveClick: noop,
    checkedEntries: [], setCheckedEntries: noop, selectedDate, onCrossDateMove: null,
    checkedUnplannedItems: [], onMoveUnplannedToRun: null, onBeforeMove: null, onServiceIssueSaved: null,
    currentBranch, simpleHeader: true, cardStyle: true, autoSave: noop, isSaving: false,
  };

  if (loading && vehicles.length === 0) {
    return (
      <div className="rounded-2xl bg-slate-950 py-12 text-center text-sm text-white/60">
        Loading transport view…
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-slate-950 p-3">
      <div className="mb-2 px-1">
        <CompactDateBar
          dates={weekDays}
          selectedDateStr={dateStr}
          onSelectDate={setSelectedDate}
          onPrev={() => setSelectedDate((d) => addDays(d, -7))}
          onNext={() => setSelectedDate((d) => addDays(d, 7))}
          isDateLocked={false}
        />
      </div>
      <div className="relative">
        <DndContext onDragEnd={noop}>
          <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
            {vehicles.length === 0 ? (
              <div className="w-full text-center text-white/50 text-sm py-10">No vehicles for this date/branch.</div>
            ) : vehicles.map((v) => (
              <div
                key={v.id}
                className="flex flex-col gap-2 rounded-2xl bg-white/10 backdrop-blur-xl border border-white/10 p-2 w-[260px] flex-shrink-0"
                data-testid={`embedded-transport-vehicle-${v.id}`}
              >
                <div className="rounded-xl shadow-lg overflow-hidden flex-shrink-0">
                  <DriverHeaderBar vehicle={v} />
                </div>
                <VehicleCard
                  {...vehicleCardCommonProps}
                  vehicle={v}
                  isViewMode
                  renderEntry={(entry, runId, runNumber, ctx) => (
                    <TransportOrderCard entry={entry} runId={runId} runNumber={runNumber} ctx={ctx} />
                  )}
                />
              </div>
            ))}
          </div>
        </DndContext>
        {/* Transparent overlay — captures any click on the vehicle cards and navigates
            to the full transport page at the currently selected date. The date strip
            above sits outside this overlay and remains interactive. */}
        {onNavigate && vehicles.length > 0 && (
          <div
            className="absolute inset-0 cursor-pointer"
            style={{ zIndex: 20 }}
            onClick={() => onNavigate(selectedDate)}
            title="Open in Transport"
          />
        )}
      </div>
    </div>
  );
};

export default EmbeddedTransportView;
